import React from 'react';
import { View, Text, StyleSheet, ScrollView, Platform } from 'react-native';
import type { LaunchSecureDto, PatinfoProfileDto, PatinfoPutRequestDto, PatinfoSearchTermDto } from '../../../types/api/patinfo';
import type { AccountPlaDto, AccountSraDto, AdminHtmlAnswerDto, MVarDto } from '../../../types/api';
import type { ClientTouchpointSchedule } from '../../../services/clients/ClientFormBuilder';
import type { ClientEnableFlags } from './ClientEnableFlags';
import { EDITOR_COLORS, editorText } from '../../../styles/editorTheme';
import { ManageClientsResultsList } from './ManageClientsResultsList';
import type { ViewReportParams } from './ProfileRowEditor';
import { ManageClientsSearchBar, type ManageClientsSearchValues } from './ManageClientsSearchBar';

interface OpenAdminsViewProps {
  profiles: PatinfoProfileDto[];
  isLoading: boolean;
  error: string | null;
  blocknum: string;
  blocksize: string;
  hasMore: boolean;
  saveSyncError: string | null;
  enableFlags: ClientEnableFlags;
  /** Backed by the same state as Manage Clients' own search bar — see ProGroupsClientsView.tsx. */
  searchValues: ManageClientsSearchValues;
  onSearchValuesChange: (values: ManageClientsSearchValues) => void;
  /** Submitting this search bar switches the active tab to Manage Clients and runs a type:'profiles' search there. */
  onSearchSubmit: (terms: PatinfoSearchTermDto[], values: ManageClientsSearchValues) => void;
  onNextPage: () => void;
  onPrevPage: () => void;
  onRefresh: () => void;
  onSaveProfile: (payload: PatinfoPutRequestDto) => Promise<void>;
  onSendNow: (profile: PatinfoProfileDto, request: LaunchSecureDto) => Promise<void>;
  onCancelLaunch: (profile: PatinfoProfileDto, request: LaunchSecureDto) => Promise<void>;
  onViewReport?: (params: ViewReportParams) => void;
  showDob?: boolean;
  sendAssessmentOptions?: AdminHtmlAnswerDto[];
  officeOnlineSraMembers?: AccountSraDto[];
  assessmentOptions?: AccountPlaDto[];
  clientPhiFields?: MVarDto[];
  genderPhiOptions?: AdminHtmlAnswerDto[];
  touchpointSchedules?: ClientTouchpointSchedule[];
}

/**
 * Currently-launched profiles (those with a pending "Next" touchpoint), chronological,
 * account-wide — same row display as Manage Clients (ManageClientsResultsList, unmodified;
 * Last Report/View Report show each profile's own most recent history entry), just backed
 * by a different search type and no search-term criteria of its own.
 */
export const OpenAdminsView: React.FC<OpenAdminsViewProps> = ({
  profiles,
  isLoading,
  error,
  blocknum,
  blocksize,
  hasMore,
  saveSyncError,
  enableFlags,
  searchValues,
  onSearchValuesChange,
  onSearchSubmit,
  onNextPage,
  onPrevPage,
  onRefresh,
  onSaveProfile,
  onSendNow,
  onCancelLaunch,
  onViewReport,
  showDob = true,
  sendAssessmentOptions = [],
  officeOnlineSraMembers = [],
  assessmentOptions = [],
  clientPhiFields = [],
  genderPhiOptions = [],
  touchpointSchedules = [],
}) => {
  const showPatId = enableFlags.enable_idmgmt;
  const content = (
    <>
      <View style={styles.searchBarWrap}>
        <ManageClientsSearchBar
          values={searchValues}
          onValuesChange={onSearchValuesChange}
          onSearch={onSearchSubmit}
          isLoading={isLoading}
          showPatId={showPatId}
          showDob={showDob}
        />
      </View>

      {saveSyncError ? (
        <View style={styles.syncErrorBox}>
          <Text style={styles.syncErrorText}>{saveSyncError}</Text>
        </View>
      ) : null}

      <ManageClientsResultsList
        profiles={profiles}
        isLoading={isLoading}
        error={error}
        blocknum={blocknum}
        blocksize={blocksize}
        hasMore={hasMore}
        enableFlags={enableFlags}
        onNextPage={onNextPage}
        onPrevPage={onPrevPage}
        onRefresh={onRefresh}
        onSaveProfile={onSaveProfile}
        onSendNow={onSendNow}
        onCancelLaunch={onCancelLaunch}
        onViewReport={onViewReport}
        sendAssessmentOptions={sendAssessmentOptions}
        officeOnlineSraMembers={officeOnlineSraMembers}
        assessmentOptions={assessmentOptions}
        clientPhiFields={clientPhiFields}
        genderPhiOptions={genderPhiOptions}
        touchpointSchedules={touchpointSchedules}
      />
    </>
  );

  if (Platform.OS !== 'web') {
    return (
      <ScrollView
        contentContainerStyle={styles.panel}
        keyboardShouldPersistTaps="handled"
        nestedScrollEnabled
      >
        {content}
      </ScrollView>
    );
  }

  return (
    <View style={styles.panel}>
      {content}
    </View>
  );
};

const styles = StyleSheet.create({
  panel: {
    padding: 20,
    paddingBottom: 32,
  },
  searchBarWrap: {
    width: '100%',
    maxWidth: 980,
    alignSelf: 'center',
  },
  syncErrorBox: {
    backgroundColor: EDITOR_COLORS.errorBackground,
    borderRadius: 10,
    padding: 12,
    marginBottom: 14,
  },
  syncErrorText: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
    lineHeight: 18,
  },
});
